import { FC, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import { Alert, Button, Drawer, Empty, Modal, Space, Typography, message } from 'antd'
import { PlusOutlined, SaveOutlined } from '@ant-design/icons'
import { translate } from '../../../core'
import {
  GetShippingCountriesQuery,
  SaveShippingMethodRulesMutation,
} from '../graphql/shipping-method.graphql'
import {
  ShippingMethod,
  ShippingMethodKind,
  ShippingMethodRule,
  ShippingRuleDraft,
  ShippingRuleEffect,
} from '../type/shipping-method'
import { ShippingRuleForm } from './ShippingRuleForm'

interface Props {
  method: ShippingMethod | null
  onClose: (changed?: boolean) => void
}

let draftSequence = 0
const nextDraftKey = () => `draft-${++draftSequence}`

function toDraft(rule: ShippingMethodRule): ShippingRuleDraft {
  return {
    draftKey: nextDraftKey(),
    label: rule.label,
    effect: rule.effect ?? ShippingRuleEffect.Include,
    countryCode: rule.countryCode,
    state: rule.state,
    minCartValue: rule.minCartValue,
    maxCartValue: rule.maxCartValue,
    priceOverride: rule.priceOverride,
    sortOrder: rule.sortOrder,
    isActive: rule.isActive,
  }
}

export const ShippingMethodRulesDrawer: FC<Props> = ({ method, onClose }) => {
  const [messageApi, contextHolder] = message.useMessage()
  const [drafts, setDrafts] = useState<ShippingRuleDraft[]>([])
  const [dirty, setDirty] = useState(false)
  const [shown, setShown] = useState<ShippingMethod | null>(null)

  useEffect(() => {
    if (!method) return
    setShown(method)
    setDrafts(method.rules.map(toDraft))
    setDirty(false)
  }, [method])

  useEffect(() => {
    if (!dirty) return
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [dirty])

  const isFallback = shown?.kind === ShippingMethodKind.Fallback

  const { data: countryData } = useQuery(GetShippingCountriesQuery, { skip: !method })
  const countries = useMemo(() => countryData?.shippingCountries ?? [], [countryData])

  const [saveRules, { loading: saving }] = useMutation(SaveShippingMethodRulesMutation, {
    onCompleted: () => {
      messageApi.success(translate('save_success_message'))
      setDirty(false)
      onClose(true)
    },
    onError: error => messageApi.error(error.message),
  })

  const patch = (draftKey: string, changes: Partial<ShippingRuleDraft>) => {
    setDrafts(prev => prev.map(d => (d.draftKey === draftKey ? { ...d, ...changes } : d)))
    setDirty(true)
  }

  const addRule = () => {
    setDrafts(prev => [...prev, { draftKey: nextDraftKey(), effect: ShippingRuleEffect.Include, isActive: true }])
    setDirty(true)
  }

  const removeRule = (draftKey: string) => {
    setDrafts(prev => prev.filter(d => d.draftKey !== draftKey))
    setDirty(true)
  }

  const handleSave = () => {
    if (!method) return

    const invalid = drafts.find(
      d => d.minCartValue != null && d.maxCartValue != null && d.minCartValue > d.maxCartValue,
    )
    if (invalid) {
      messageApi.error(translate('min_cart_value_exceeds_max'))
      return
    }

    const unconditional = drafts.find(
      d => d.effect === ShippingRuleEffect.Exclude
        && !d.countryCode && !d.state && d.minCartValue == null && d.maxCartValue == null,
    )
    if (unconditional) {
      messageApi.error(translate('exclusion_needs_condition'))
      return
    }

    saveRules({
      variables: {
        shippingMethodId: method.id,
        dto: {
          rules: drafts.map(({ draftKey, ...rule }, index) => ({
            ...rule,
            sortOrder: index,
            isActive: rule.isActive ?? true,
          })),
        },
      },
    })
  }

  const handleClose = () => {
    if (!dirty) {
      onClose()
      return
    }
    Modal.confirm({
      title: translate('unsaved_changes'),
      content: translate('discard_unsaved_confirm'),
      okText: translate('discard'),
      cancelText: translate('cancel'),
      okButtonProps: { danger: true },
      onOk: () => onClose(),
    })
  }

  return (
    <Drawer
      open={!!method}
      width={720}
      onClose={handleClose}
      title={
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{translate('availability_rules')}</Typography.Text>
          <Typography.Text type="secondary">{shown?.title}</Typography.Text>
        </Space>
      }
      extra={isFallback ? null : (
        <Space>
          <Button icon={<PlusOutlined />} onClick={addRule}>
            {translate('add', 'rule')}
          </Button>
          <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSave}>
            {translate('save')}
          </Button>
        </Space>
      )}
    >
      {contextHolder}

      {isFallback ? (
        <Alert
          type="warning"
          showIcon
          message={translate('fallback_has_no_rules')}
          description={translate('fallback_has_no_rules_description')}
        />
      ) : (
        <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={translate('shipping_rules_semantics')}
      />

      {drafts.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={translate('shipping_no_rules_hint')}
        />
      ) : (
        drafts.map((rule, index) => (
          <ShippingRuleForm
            key={rule.draftKey}
            rule={rule}
            index={index}
            countries={countries}
            basePrice={shown?.basePrice ?? 0}
            onChange={changes => patch(rule.draftKey, changes)}
            onRemove={() => removeRule(rule.draftKey)}
          />
        ))
      )}
        </>
      )}
    </Drawer>
  )
}
